import type { GameRow, Period, PeriodMoneyline, TradeTicketContext, VenueBook } from '../ValueDashboard/types';

type PeriodSelectors = {
  getPeriodMoneyline: (gameKey: string, period: Period) => PeriodMoneyline | null;
  getBtts: (gameKey: string, period: Period) => { kalshi: VenueBook | null; polymarket: VenueBook | null } | null;
};

/** NHL period singles have no ladder strip to keep the ticket current.
 * Resolve from the existing selectors on each store version, retaining the
 * exact selected native contract and side. Null holds an unavailable target;
 * undefined leaves unrelated journeys to their existing resolver. */
export function resolveLiveNhlPeriodPanelGame(
  game: GameRow | null,
  selection: Pick<TradeTicketContext, 'game' | 'teamSide' | 'venue'>,
  selectors: PeriodSelectors,
): GameRow | null | undefined {
  const snapshot = selection.game;
  if (snapshot.sport !== 'nhl') return undefined;
  const scope = snapshot.key.match(/^(.+)\|([1-3]P)-(ml|ml-tie|btts)$/);
  if (!scope) return undefined;
  if (!game || game.sport !== 'nhl' || game.key !== scope[1] || selection.venue !== 'kalshi') return null;
  const ticker = snapshot.kalshi?.[selection.teamSide].marketTicker;
  if (!ticker) return null;
  const period = scope[2] as Period;
  let pair: GameRow['kalshi'] = null;
  if (scope[3] === 'btts') {
    const book = selectors.getBtts(game.key, period)?.kalshi;
    if (book) pair = { away: book, home: book };
  } else {
    const ml = selectors.getPeriodMoneyline(game.key, period);
    if (!ml || ml.gameKey !== game.key || ml.period !== period) return null;
    if (scope[3] === 'ml-tie') {
      if (ml.tie.kalshi) pair = { away: ml.tie.kalshi, home: ml.tie.kalshi };
    } else if (ml.away.kalshi && ml.home.kalshi) {
      pair = { away: ml.away.kalshi, home: ml.home.kalshi };
    }
  }
  if (!pair || pair[selection.teamSide].marketTicker !== ticker) return null;
  // Preserve row identity/wording, never the old book or another venue's book.
  return { ...snapshot, kalshi: pair, polymarket: null };
}
